import React, { useMemo, useState, useEffect } from "react";
import { Box, Grid, Typography, Button, alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";

import Filters from "../components/Filters";
import { useMarketFeedInstant } from "../../hooks/useMarketFeedInstant";
import UnifiedMarketCard from "../../components/UnifiedMarketCard";

const WARMED = new Set();
const warmImage = (url) => {
  if (!url) return;
  if (WARMED.has(url)) return;
  WARMED.add(url);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
};

function slugifyTitle(title) {
  const base = String(title || "event")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return base || "event";
}

export default function Market() {
  const navigate = useNavigate();

  const [searchForecasts, setSearchForecasts] = useState("");
  const [topForecasts, setTopForecasts] = useState("All");
  const [chipForecasts, setChipForecasts] = useState("All");
  const [subForecasts, setSubForecasts] = useState("All");
  const [visibleForecasts, setVisibleForecasts] = useState(8);

  const { predictions: tasksRaw, loadingPredictions } = useMarketFeedInstant({ pageSize: 300 });

  const normalizeStr = (v) => String(v || "").toLowerCase().trim();

  const itemMatches = (item, value) => {
    if (!value || value === "All") return true;
    const w = normalizeStr(value);

    const fields = [
      item.market,
      item.category,
      item.sector,
      item.name,
      item.title,
      item.handle,
      item.creator,
      item.companyName,
      item.marketChip,
      item.marketSub,
      item.type,
      ...(item.tags || []),
      ...(item.assets || []),
    ]
      .filter(Boolean)
      .map(normalizeStr);

    return fields.some((x) => x === w || x.includes(w));
  };

  const applyFilters = (items, { q, top, chip, sub }) => {
    let out = items;
    if (top && top !== "All") out = out.filter((x) => itemMatches(x, top));
    if (chip && chip !== "All") out = out.filter((x) => itemMatches(x, chip));
    if (sub && sub !== "All") out = out.filter((x) => itemMatches(x, sub));
    const s = normalizeStr(q);
    if (s) out = out.filter((x) => itemMatches(x, s));
    return out;
  };

  const goToPredictionPage = (item) => {
    if (!item?.id) return;
    const title = item.name || item.title || item.marketTitle || "event";
    const slug = slugifyTitle(title);
    navigate(`/event/${slug}-${item.id}`);
  };

  const goToPrediction = (item, side) => {
    const title = (item.name || item.title || "prediction")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    navigate(`/prediction/${title}-${item.id}?side=${side}&stake=100`);
  };

  const forecastTasksBase = useMemo(() => {
    const list = [...(tasksRaw || [])];
    list.sort((a, b) => Number(b.top) - Number(a.top) || Number(b.views || 0) - Number(a.views || 0));
    return list;
  }, [tasksRaw]);

  const forecastTasksFiltered = useMemo(() => {
    return applyFilters(forecastTasksBase, {
      q: searchForecasts,
      top: topForecasts,
      chip: chipForecasts,
      sub: subForecasts,
    });
  }, [forecastTasksBase, searchForecasts, topForecasts, chipForecasts, subForecasts]);

  const pagedForecasts = useMemo(
    () => (forecastTasksFiltered || []).slice(0, visibleForecasts),
    [forecastTasksFiltered, visibleForecasts]
  );

  useEffect(() => {
    const first = (forecastTasksFiltered || []).slice(0, 12);
    first.forEach((x) => {
      const url = x.posterLow || x.poster || x.imageLow || x.imageSrc || "";
      warmImage(url);
    });
  }, [forecastTasksFiltered]);

  const predSkeleton = useMemo(
    () =>
      Array.from({ length: 8 }).map((_, i) => ({
        id: `sk_pred_${i}`,
        type: "prediction",
        name: " ",
        handle: " ",
        category: " ",
        flags: {},
        avatarUrl: "",
        imageSrc: "",
        imageLow: "",
        poster: "",
        posterLow: "",
        yesBets: 0,
        noBets: 0,
        views: 0,
        description: " ",
      })),
    []
  );

  const showPredSkeleton = loadingPredictions && pagedForecasts.length === 0;

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Filters
        searchQuery={searchForecasts}
        setSearchQuery={setSearchForecasts}
        marketTop={topForecasts}
        setMarketTop={setTopForecasts}
        marketChip={chipForecasts}
        setMarketChip={setChipForecasts}
        marketSub={subForecasts}
        setMarketSub={setSubForecasts}
      />

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 0.5 }}>
        {(showPredSkeleton ? predSkeleton : pagedForecasts).map((task, idx) => (
          <Grid item key={task.id} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
            <Box sx={{ width: "100%", display: "flex", minWidth: 0, "& > *": { flex: 1, minWidth: 0 } }}>
              <UnifiedMarketCard
                item={{ ...task, type: "prediction" }}
                priority={idx < 4}
                skeleton={showPredSkeleton}
                onOpen={() => goToPredictionPage(task)}
                onPrimary={() => goToPredictionPage(task)}
                onTips={(x) => console.log("tips", x)}
                onBoost={(x) => console.log("boost", x)}
                onYes={() => goToPrediction(task, "yes")}
                onNo={() => goToPrediction(task, "no")}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {!showPredSkeleton && visibleForecasts < forecastTasksFiltered.length && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setVisibleForecasts((p) => p + 8)}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              color: "#e5e7eb",
              borderColor: "rgba(255,255,255,.16)",
              "&:hover": { borderColor: "rgba(56,189,248,.35)", background: "rgba(56,189,248,.06)" },
            }}
          >
            Show more
          </Button>
        </Box>
      )}

      {!showPredSkeleton && pagedForecasts.length === 0 && (
        <Box sx={{ textAlign: "center", color: alpha("#e5e7eb", 0.7), py: 6 }}>
          <Typography sx={{ fontWeight: 900, mb: 0.75 }}>No forecasts</Typography>
          <Typography variant="body2">No items for this market filter.</Typography>
        </Box>
      )}
    </Box>
  );
}
